import { useCallback, useEffect } from 'react';
import { BlockerFunction, useBlocker, useNavigate } from 'react-router-dom';

import { PlayerContextState } from '../contexts/player';

type GuardResult = {
  block: boolean;
  redirectTo?: string;
};

const resolveNavigationGuard = (
  pathname: string,
  state: PlayerContextState
): GuardResult => {
  const player = state.playerState;
  const gameCode = state.gameCode;

  const hasIdentity = !!player?.name && !!player?.avatarImage;

  // /preload/:gameCode
  // /games/:gameCode (root)
  if (
    pathname === `/games/${gameCode}/` ||
    pathname === `/games/${gameCode}` ||
    pathname.includes('/preload')
  ) {
    if (!player) return { block: false };
    if (!hasIdentity) return { block: false };
    if (player.status === 'waiting') return { block: false };

    return {
      block: true,
      redirectTo:
        player.status === 'ready'
          ? `/games/${gameCode}/lobby`
          : `/games/${gameCode}/map`
    };
  }

  // /games/:gameCode/lobby
  if (pathname.includes('/lobby')) {
    if (
      hasIdentity &&
      (player.status === 'waiting' || player.status === 'ready')
    )
      return { block: false };

    return {
      block: true,
      redirectTo: `/games/${gameCode}`
    };
  }

  // /games/:gameCode/map
  if (pathname.includes('/map')) {
    if (
      hasIdentity &&
      (player.status === 'ready' || player?.status === 'playing')
    )
      return { block: false };

    return {
      block: true,
      redirectTo: `/games/${gameCode}/lobby`
    };
  }

  // assessment & others
  return { block: false };
};

export const usePlayerNavigationGuard = (state: PlayerContextState) => {
  const navigate = useNavigate();

  const shouldBlock = useCallback<BlockerFunction>(
    ({ nextLocation }) => {
      const { block } = resolveNavigationGuard(nextLocation.pathname, state);

      return block;
    },
    [state.playerState, state.gameCode]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const { redirectTo } = resolveNavigationGuard(
      blocker.location.pathname,
      state
    );

    if (redirectTo) navigate(redirectTo, { replace: true });

    blocker.reset();
  }, [blocker, state.playerState, state.gameCode]);
};
